import Numbers from "@mui/icons-material/Numbers";
import { Box, Button, Tooltip, type ButtonProps } from "@mui/material";

import { useSncrNumeracoesDrawerOptional } from "../context/SncrNumeracoesDrawerContext";
import { useSncrDisponiveisCount } from "../hooks/useSncrDisponiveisCount";

type Props = {
  label?: string;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  color?: ButtonProps["color"];
  showCount?: boolean;
};

export function SncrOpenDrawerButton({
  label = "Gestão SNCR",
  size = "small",
  variant = "outlined",
  color = "inherit",
  showCount = true,
}: Props) {
  const drawer = useSncrNumeracoesDrawerOptional();
  const { disponiveis } = useSncrDisponiveisCount({
    enabled: Boolean(drawer) && showCount,
    refreshKey: drawer?.open === false ? "closed" : "open",
  });

  if (!drawer) return null;

  const countKnown = typeof disponiveis === "number";
  const stockTone =
    !countKnown || disponiveis === 0
      ? "error.main"
      : disponiveis < 10
        ? "warning.main"
        : "success.main";
  const stockContrast =
    !countKnown || disponiveis === 0
      ? "error.contrastText"
      : disponiveis < 10
        ? "warning.contrastText"
        : "success.contrastText";

  const countLabel = !countKnown
    ? null
    : disponiveis === 0
      ? "Sem estoque"
      : `${disponiveis} disp.`;

  const ariaLabel = countKnown
    ? `Abrir gestão SNCR — ${disponiveis} disponível${disponiveis === 1 ? "" : "eis"}`
    : "Abrir gestão de numerações SNCR (Anvisa)";

  return (
    <Tooltip title="Abrir gestão de numerações SNCR (Anvisa)" arrow>
      <Button
        size={size}
        variant={variant}
        color={color}
        aria-label={ariaLabel}
        startIcon={<Numbers fontSize="small" />}
        onClick={drawer.openSncrNumeracoes}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          flexShrink: 0,
          gap: 0.75,
          pl: 1.25,
          pr: countLabel ? 0.75 : 1.5,
          py: 0.4,
          minHeight: 32,
          borderColor: "divider",
          "&:hover": {
            borderColor: "text.secondary",
            bgcolor: "action.hover",
          },
        }}
      >
        {label}
        {countLabel ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.2,
              ml: 0.25,
              borderRadius: 999,
              fontSize: "0.7rem",
              fontWeight: 800,
              lineHeight: 1.4,
              bgcolor: stockTone,
              color: stockContrast,
            }}
          >
            {countLabel}
          </Box>
        ) : null}
      </Button>
    </Tooltip>
  );
}
